import { useEffect, useState } from "react";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import FileAClaimContactUs from "components/General/FileAClaimContactUs";

import FACStepOne from "components/AgentPortal/FileAClaim/FACStepOne";
import FACStepTwo from "components/AgentPortal/FileAClaim/FACStepTwo";
import FACStepThree from "components/AgentPortal/FileAClaim/FACStepThree";
import FACStepFour from "components/AgentPortal/FileAClaim/FACStepFour";
import FACStepFive from "components/AgentPortal/FileAClaim/FACStepFive";
import FACStepSix from "components/AgentPortal/FileAClaim/FACStepSix";

export default function FileAClaim() {
  const [selectedPolicy, setSelectedPolicy] = useState({});
  const [formStep, setFormStep] = useState(1);
  const [FACFormData, setFACFormData] = useState({});
  const [loggedUser, setLoggedUser] = useState();
  const [contractID, setContractID] = useState("");
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const contractID = searchParams.get("contractID");
    if (contractID) {
      setContractID(contractID);
      setSelectedPolicy(contractID);
    }
  }, [searchParams]);

  useBreadcrumb([
    // agent BC
    { name: "Home", url: "/dashboard" },
    { name: "Services and Claims", url: "/services-claims" },
    { name: "File a Claim", url: "#" },
  ]);

  return (
    <>
      <div className="container mb-8">
        <h1>{t("FNOL-title")}</h1>
        <p>{t("FNOL-description")}</p>
        <hr />
      </div>

      <div className="container ui-component-spacing">
        <div className="grid gap-4 grid-cols-1 lg:gap-6 lg:grid-cols-[66.66667%,33.33333%]">
          <div>
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              {/* intro card */}
              <div className="ui-card ui-rte-component p-4 xl:p-6">
                <h2 className="text-xl font-body font-bold">
                  {t("FNOL-cardTitle")}
                </h2>

                <hr className="mt-4 mb-6" />

                <div className="text-sm">
                  <p className="font-semibold">
                    {t("FNOL-agent-cardSubTitle")}
                  </p>

                  <ul className="mt-4 list-disc list-outside ml-8">
                    <li>{t("FNOL-agent-card-List-1")}</li>
                    <li>{t("FNOL-agent-cardList-2")}</li>
                    <li>{t("FNOL-agent-cardList-3")}</li>
                    <li>{t("FNOL-agent-cardList-4")}</li>
                    <li>{t("FNOL-agent-cardList-5")}</li>
                    <li>{t("FNOL-agent-cardList-6")}</li>
                    <li>{t("FNOL-agent-cardList-7")}</li>
                  </ul>
                </div>
              </div>

              <div>
                {/* step 1 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-6 mb-4">
                      <div className="inline-flex justify-center items-center h-8 w-8 text-white bg-ui-blue-dark rounded-full overflow-hidden">
                        1
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>

                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-step-Title1")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 1
                        </div>
                      </div>
                      <p>{t("FNOL-stepSub-Title1")}</p>
                      <FACStepOne
                        selectedPolicy={selectedPolicy}
                        setSelectedPolicy={setSelectedPolicy}
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                        setLoggedUser={setLoggedUser}
                        contractID={contractID}
                      />
                    </div>
                  </div>
                </div>

                {/* step 2 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 1
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        2
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle2")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 2
                        </div>
                      </div>

                      <FACStepTwo
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                {/* step 3 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 2
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        3
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle5")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 3
                        </div>
                      </div>

                      <FACStepThree
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                {/* step 4 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 3
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        4
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle3")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 4
                        </div>
                      </div>

                      <FACStepFour
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 4
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        5
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle6")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 5
                        </div>
                      </div>

                      <FACStepFive
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 5
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        6
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-transparent overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle4")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 6
                        </div>
                      </div>

                      <FACStepSix
                        formStep={formStep}
                        setFormStep={setFormStep}
                        FACFormData={FACFormData}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FileAClaimContactUs showOtherContact={false} />
        </div>
      </div>
    </>
  );
}
